import React, { useEffect, useState, useRef } from 'react';
import { useParams, useLocation } from 'react-router';
import PropTypes from 'prop-types';
import { utils, DicomMetadataStore } from '@ohif/core';
import { ImageViewerProvider, DragAndDropProvider } from '@ohif/ui-next';
import { useSearchParams } from '../../hooks';
import { useAppConfig } from '@state';
import ViewportGrid from '@components/ViewportGrid';
import Compose from './Compose';
import loadModules from '../../pluginImports';
import { defaultRouteInit } from './defaultRouteInit';
import { updateAuthServiceAndCleanUrl } from './updateAuthServiceAndCleanUrl';
import helicalLogo from '../../../assets/logo.png';

const { getSplitParam } = utils;

export default function ModeRoute({
  mode,
  dataSourceName,
  extensionManager,
  servicesManager,
  commandsManager,
  hotkeysManager,
}: withAppTypes) {
  const [appConfig] = useAppConfig();

  // Parse route params/querystring
  const location = useLocation();

  // The react router DOM placeholder map (see https://reactrouter.com/en/main/hooks/use-params).
  const params = useParams();
  // The URL's query search parameters where the keys casing is maintained
  const query = useSearchParams();

  mode?.onModeInit?.({
    servicesManager,
    extensionManager,
    commandsManager,
    appConfig,
    query,
  });

  // The URL's query search parameters where the keys are all lower case.
  const lowerCaseSearchParams = useSearchParams({ lowerCaseKeys: true });

  const [studyInstanceUIDs, setStudyInstanceUIDs] = useState(null);
  const [dataSourceError, setDataSourceError] = useState(null);

  const [refresh, setRefresh] = useState(false);
  const [ExtensionDependenciesLoaded, setExtensionDependenciesLoaded] = useState(false);

  const [initialLoadComplete, setInitialLoadComplete] = useState(
    () => !!(window as any).__ohifInitialLoadComplete
  );
  const [initProgress, setInitProgress] = useState(0);
  const [initMessage, setInitMessage] = useState('');

  const layoutTemplateData = useRef(false);
  const locationRef = useRef(null);
  const isMounted = useRef(false);

  if (location !== locationRef.current) {
    layoutTemplateData.current = null;
    locationRef.current = location;
  }

  const {
    displaySetService,
    panelService,
    hangingProtocolService,
    userAuthenticationService,
    customizationService,
  } = servicesManager.services;

  const { extensions, sopClassHandlers, hangingProtocol } = mode;

  const runTimeHangingProtocolId = lowerCaseSearchParams.get('hangingprotocolid');
  const runTimeStageId = lowerCaseSearchParams.get('stageid');
  const token = lowerCaseSearchParams.get('token');

  if (token) {
    updateAuthServiceAndCleanUrl(token, location, userAuthenticationService);
  }

  // An undefined dataSourceName implies that the active data source that is already set in the ExtensionManager should be used.
  if (dataSourceName !== undefined) {
    extensionManager.setActiveDataSource(dataSourceName);
  }

  const dataSource = extensionManager.getActiveDataSourceOrNull();

  // Only handling one route per mode for now
  const route = mode.routes?.[0] ?? null;

  useEffect(() => {
    const loadExtensions = async () => {
      const loadedExtensions = await loadModules(Object.keys(extensions));
      for (const extension of loadedExtensions) {
        const { id: extensionId } = extension;
        if (extensionManager.registeredExtensionIds.indexOf(extensionId) === -1) {
          await extensionManager.registerExtension(extension);
        }
      }

      if (isMounted.current) {
        setExtensionDependenciesLoaded(true);
      }
    };

    loadExtensions();
  }, []);

  useEffect(() => {
    // Preventing state update for unmounted component
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Listen for loading progress events from data source
  useEffect(() => {
    if (initialLoadComplete) {
      return;
    }
    const handler = (e: any) => {
      if (!isMounted.current) {
        return;
      }
      const { percent, message } = e.detail || {};
      if (typeof percent === 'number') {
        setInitProgress(percent);
      }
      if (typeof message === 'string') {
        setInitMessage(message);
      }
    };
    window.addEventListener('dicom-loading-progress', handler);
    return () => window.removeEventListener('dicom-loading-progress', handler);
  }, [initialLoadComplete]);

  useEffect(() => {
    if (!ExtensionDependenciesLoaded) {
      return;
    }

    // Todo: this should not be here, data source should not care about params
    const initializeDataSource = async (params, query) => {
      try {
        // For dicomlocal: data lives only in browser memory.
        // On page refresh DicomMetadataStore is empty, so redirect back to
        // RemoteLocal to re-download the files from the manifest.
        if (dataSourceName === 'dicomlocal') {
          const storeUIDs = DicomMetadataStore.getStudyInstanceUIDs();
          if (!storeUIDs || storeUIDs.length === 0) {
            // RemoteLocal reload: redirect back to /remotelocal to re-download from manifest
            const savedPatientId = sessionStorage.getItem('remotelocal_patientId');
            const savedFolderId = sessionStorage.getItem('remotelocal_folderId');
            if (savedFolderId && savedPatientId) {
              const redirectParams = new URLSearchParams();
              redirectParams.set('patientId', savedPatientId);
              redirectParams.set('folderId', savedFolderId);
              window.location.replace(`/remotelocal?${redirectParams.toString()}`);
              return;
            }

            // HelicalCloud reload: redirect back to /helicalcloud to re-download from cloud
            const savedCase = sessionStorage.getItem('helicalcloud_case');
            const savedVt = sessionStorage.getItem('helicalcloud_vt');
            const savedApi = sessionStorage.getItem('helicalcloud_api');
            if (savedCase && savedApi) {
              const redirectParams = new URLSearchParams();
              redirectParams.set('case', savedCase);
              redirectParams.set('api', savedApi);
              if (savedVt) redirectParams.set('vt', savedVt);
              const savedMode = sessionStorage.getItem('helicalcloud_mode') || 'helicalcloud';
              const savedStudy = sessionStorage.getItem('helicalcloud_study');
              redirectParams.set('mode', savedMode);
              if (savedStudy) redirectParams.set('study', savedStudy);
              window.location.replace(`/helicalcloud?${redirectParams.toString()}`);
              return;
            }
          }
        }

        await dataSource.initialize({
          params,
          query,
        });
        setStudyInstanceUIDs(dataSource.getStudyInstanceUIDs({ params, query }));
      } catch (err) {
        console.error('[Mode] Data source initialization failed:', err);
        setDataSourceError(err.message || 'Failed to load study data');
      }
    };

    initializeDataSource(params, query);
    return () => {
      layoutTemplateData.current = null;
    };
  }, [location, ExtensionDependenciesLoaded]);

  useEffect(() => {
    if (!ExtensionDependenciesLoaded || !studyInstanceUIDs?.length) {
      return;
    }

    const retrieveLayoutData = async () => {
      const layoutData = await route.layoutTemplate({
        location,
        servicesManager,
        studyInstanceUIDs,
      });

      if (isMounted.current) {
        const { leftPanels = [], rightPanels = [], ...layoutProps } = layoutData.props;

        panelService.reset();
        panelService.addPanels(panelService.PanelPosition.Left, leftPanels);
        panelService.addPanels(panelService.PanelPosition.Right, rightPanels);

        // layoutProps contains all props but leftPanels and rightPanels
        layoutData.props = layoutProps;

        layoutTemplateData.current = layoutData;
        setRefresh(!refresh);
      }
    };
    if (Array.isArray(studyInstanceUIDs) && studyInstanceUIDs[0]) {
      retrieveLayoutData();
    }
    return () => {
      layoutTemplateData.current = null;
    };
  }, [studyInstanceUIDs, ExtensionDependenciesLoaded]);

  useEffect(() => {
    if (!layoutTemplateData.current || !ExtensionDependenciesLoaded || !studyInstanceUIDs?.length) {
      return;
    }

    const setupRouteInit = async () => {
      // TODO: For some reason this is running before the Providers
      // are calling setServiceImplementation
      // TODO -> iterate through services.

      // Extension

      // Add SOPClassHandlers to a new SOPClassManager.
      displaySetService.init(extensionManager, sopClassHandlers);

      extensionManager.onModeEnter({
        servicesManager,
        extensionManager,
        commandsManager,
        appConfig,
      });

      // use the URL hangingProtocolId if it exists, otherwise use the one
      // defined in the mode configuration
      const hangingProtocolIdToUse = hangingProtocolService.getProtocolById(
        runTimeHangingProtocolId
      )
        ? runTimeHangingProtocolId
        : hangingProtocol;

      // Determine the index of the stageId if the hangingProtocolIdToUse is defined
      const stageIndex = Array.isArray(hangingProtocolIdToUse)
        ? -1
        : hangingProtocolService.getStageIndex(hangingProtocolIdToUse, {
            stageId: runTimeStageId || undefined,
          });
      // Ensure that the stage index is never negative
      // If stageIndex is negative (e.g., if stage wasn't found), use 0 as the default
      const stageIndexToUse = Math.max(0, stageIndex);

      // Sets the active hanging protocols - if hangingProtocol is undefined,
      // resets to default.  Done before the onModeEnter to allow the onModeEnter
      // to perform custom hanging protocol actions
      hangingProtocolService.setActiveProtocolIds(hangingProtocolIdToUse);

      mode?.onModeEnter({
        servicesManager,
        extensionManager,
        commandsManager,
        appConfig,
      });

      // Move hotkeys setup here, after onModeEnter
      const hotkeys = customizationService.getCustomization('ohif.hotkeyBindings');
      hotkeysManager.setDefaultHotKeys(hotkeys);

      /**
       * The next line should get all the query parameters provided by the URL
       * - except the StudyInstanceUIDs - and create an object called filters
       * used to filtering the study as the user wants otherwise it will return
       * a empty object.
       *
       * Example:
       * const filters = {
       *   seriesInstanceUID: 1.2.276.0.7230010.3.1.3.1791068887.5412.1620253993.114611
       * }
       */
      const filters =
        Array.from(query.keys()).reduce((acc: Record<string, string>, val: string) => {
          const lowerVal = val.toLowerCase();
          // Not sure why the case matters here - it doesn't in the URL
          if (lowerVal === 'seriesinstanceuids' || lowerVal === 'seriesinstanceuid') {
            const seriesUIDs = getSplitParam(lowerVal, query);
            return {
              ...acc,
              seriesInstanceUID: seriesUIDs,
            };
          }
          return { ...acc, [val]: getSplitParam(lowerVal, query) };
        }, {}) ?? {};

      let unsubs;

      if (route.init) {
        unsubs = await route.init(
          {
            servicesManager,
            extensionManager,
            hotkeysManager,
            studyInstanceUIDs,
            dataSource,
            filters,
          },
          hangingProtocolIdToUse,
          stageIndexToUse
        );
      }

      return defaultRouteInit(
        {
          servicesManager,
          studyInstanceUIDs,
          dataSource,
          filters,
          appConfig,
        },
        hangingProtocolIdToUse,
        stageIndexToUse
      );
    };

    let unsubscriptions;
    setupRouteInit().then(unsubs => {
      unsubscriptions = unsubs;

      mode?.onSetupRouteComplete?.({
        servicesManager,
        extensionManager,
        commandsManager,
      });

      if (isMounted.current) {
        setInitProgress(100);
        setInitMessage('Ready');
        (window as any).__ohifInitialLoadComplete = true;
        setInitialLoadComplete(true);
      }
    });

    return () => {
      // The mode.onModeExit must be done first to allow it to store
      // information, and must be in a try/catch to ensure subscriptions
      // are unsubscribed.
      try {
        mode?.onModeExit?.({
          servicesManager,
          extensionManager,
          appConfig,
        });
      } catch (e) {
        console.warn('mode exit failure', e);
      }
      // Clean up hotkeys
      hotkeysManager.destroy();

      // The unsubscriptions must occur before the extension onModeExit
      // in order to prevent exceptions during cleanup caused by spurious events
      if (unsubscriptions) {
        unsubscriptions.forEach(unsub => {
          unsub();
        });
      }
      // The extension manager must be called after the mode, this is
      // expected to cleanup the state to a standard setup.
      extensionManager.onModeExit();
    };
  }, [
    mode,
    dataSourceName,
    location,
    ExtensionDependenciesLoaded,
    route,
    servicesManager,
    extensionManager,
    hotkeysManager,
    studyInstanceUIDs,
    refresh,
  ]);

  // Error screen
  if (dataSourceError) {
    return (
      <div className="flex h-screen w-screen flex-col" style={{ backgroundColor: 'bg-background' }}>
        <div className="relative h-[120px] flex items-center" style={{ backgroundColor: 'bg-background', borderBottom: '1px solid #1e1a2b' }}>
          <div className="ml-3">
            <img
              src={helicalLogo}
              alt="Helical"
              style={{ height: '120px', width: '120px', objectFit: 'contain', cursor: 'pointer', opacity: 0.95 }}
              onClick={() => window.location.href = '/'}
            />
          </div>
        </div>
        <div className="flex flex-1 items-center justify-center" style={{ backgroundColor: '#000' }}>
          <div className="text-center">
            <p className="text-[#e2e8f0] text-base font-medium">No study data available</p>
            <p className="text-[#64748b] mt-2 text-sm max-w-sm">
              {dataSourceError.includes('404')
                ? 'The requested study could not be found. Please ensure the DICOM server is running and the study has been uploaded.'
                : 'Something went wrong while loading the study. Please try again or contact support if the issue persists.'}
            </p>
            <div className="mt-6">
              <button
                className="rounded px-5 py-1.5 text-sm text-[#e2e8f0] transition-colors"
                style={{ backgroundColor: '#1e1a2b' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#2b2540'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#1e1a2b'}
                onClick={() => window.history.length > 1 ? window.history.back() : window.location.href = '/'}
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Helper: renders the Helical loading screen
  const renderLoadingScreen = () => {
    const clampedProgress = Math.min(100, Math.max(0, initProgress));
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          backgroundColor: 'bg-background',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img
          src={helicalLogo}
          alt="Helical"
          style={{ height: '80px', marginBottom: '40px', opacity: 0.95 }}
        />
        <div
          style={{
            width: '280px',
            height: '6px',
            backgroundColor: '#1e1a2b',
            borderRadius: '3px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${clampedProgress}%`,
              background: 'linear-gradient(90deg, #6c5ce7, #8b7cf0)',
              borderRadius: '3px',
              transition: 'width 0.3s ease',
            }}
          />
        </div>
        <div
          style={{
            marginTop: '12px',
            fontSize: '14px',
            fontWeight: 600,
            color: '#e2e8f0',
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          {clampedProgress}%
        </div>
        {initMessage && (
          <div
            style={{
              marginTop: '6px',
              fontSize: '12px',
              color: '#64748b',
              fontFamily: 'system-ui, sans-serif',
            }}
          >
            {initMessage}
          </div>
        )}
      </div>
    );
  };

  // Phase 1: Data not ready yet (server fetch in progress, extensions loading, etc.)
  // Show Helical loading screen so user sees logo + progress, NOT a blank page
  if (!studyInstanceUIDs || !layoutTemplateData.current || !ExtensionDependenciesLoaded) {
    if (!initialLoadComplete) {
      return renderLoadingScreen();
    }
    return null;
  }

  const ViewportGridWithDataSource = props => {
    return ViewportGrid({ ...props, dataSource });
  };

  const CombinedExtensionsContextProvider = createCombinedContextProvider(
    extensionManager,
    servicesManager,
    commandsManager
  );

  const getLayoutComponent = props => {
    const layoutTemplateModuleEntry = extensionManager.getModuleEntry(
      layoutTemplateData.current.id
    );
    const LayoutComponent = layoutTemplateModuleEntry.component;

    return <LayoutComponent {...props} />;
  };

  const LayoutComponent = getLayoutComponent({
    ...layoutTemplateData.current.props,
    ViewportGridComp: ViewportGridWithDataSource,
  });

  // Phase 2: Data ready, viewer mounting behind overlay.
  // Overlay covers everything so user still sees loading screen while
  // hanging protocol and viewports initialize.
  return (
    <>
      {!initialLoadComplete && renderLoadingScreen()}
      <ImageViewerProvider StudyInstanceUIDs={studyInstanceUIDs}>
        {CombinedExtensionsContextProvider ? (
          <CombinedExtensionsContextProvider>
            <DragAndDropProvider>{LayoutComponent}</DragAndDropProvider>
          </CombinedExtensionsContextProvider>
        ) : (
          <DragAndDropProvider>{LayoutComponent}</DragAndDropProvider>
        )}
      </ImageViewerProvider>
    </>
  );
}

/**
 * Creates a combined context provider using the context modules from the extension manager.
 * @param {object} extensionManager - The extension manager instance.
 * @param {object} servicesManager - The services manager instance.
 * @param {object} commandsManager - The commands manager instance.
 * @returns {React.Component} - A React component that provides combined contexts to its children.
 */
function createCombinedContextProvider(extensionManager, servicesManager, commandsManager) {
  const extensionsContextModules = extensionManager.getModulesByType(
    extensionManager.constructor.MODULE_TYPES.CONTEXT
  );

  if (!extensionsContextModules?.length) {
    return;
  }

  const contextModuleProviders = extensionsContextModules.flatMap(({ module }) => {
    return module.map(aContextModule => {
      return aContextModule.provider;
    });
  });

  return ({ children }) => {
    return Compose({ components: contextModuleProviders, children });
  };
}

ModeRoute.propTypes = {
  mode: PropTypes.object.isRequired,
  dataSourceName: PropTypes.string,
  extensionManager: PropTypes.object,
  servicesManager: PropTypes.object,
  hotkeysManager: PropTypes.object,
  commandsManager: PropTypes.object,
};
